import MandatoryLabel from "@/app/components/ui/Reusable/MandatoryLabel";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import Toggle from "@/app/components/ui/Toggles";
import { toast } from "react-hot-toast";
import { surgeKey, surgeSlotPayload, useBookingCartPreviewAPI } from "@/app/hooks/api_hooks/bookings/useBookingCartPreviewAPI";
import { useBookingDiscountCouponListAPI } from "@/app/hooks/api_hooks/bookings/useBookingDiscountCouponListAPI";
import { useBookingMembershipOptionsAPI } from "@/app/hooks/api_hooks/bookings/useBookingMembershipOptionsAPI";
import { useBookingSurgeryOptionsAPI } from "@/app/hooks/api_hooks/bookings/useBookingSurgeryOptionsAPI";
import { useBookingPurchaseOptionsAPI } from "@/app/hooks/api_hooks/bookings/useBookingPurchaseOptionsAPI";
import { useBookingSummaryAPI } from "@/app/hooks/api_hooks/bookings/useBookingSummaryAPI";
import { useLateBookingCheckAPI } from "@/app/hooks/api_hooks/bookings/useLateBookingCheckAPI";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useRBACFlag } from "@/app/hooks/useRBACFlag";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState, setPetDetails } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import {
  ISlotsState,
  setLateBookingFlag,
  setLateBookingStartTime,
  setSelectedPurchaseAddOnIds,
  setSelectedPurchaseMembershipId,
  setSelectedPurchaseServiceId,
  setSelectedVaccineCoupon,
  setSelectedVaccineCouponMap,
  setSelectedAddOnCoupon,
  setSelectedPurchaseVaccineIds,
  setSlotBookingFilters,
  setSlotsComments,
  setUserAddress,
  toggleSelectedPurchaseAddOnId,
  toggleSelectedPurchaseVaccineId,
  setSelectedMembershipCouponId,
  setSelectedMembershipCouponName,
  setSelectedSurgeryIds,
  setSelectedSurgeryCoupon,
  setSelectedPurchaseDiagnosticIds,
  setSelectedPurchaseOtherTestIds,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import { Dialog, Switch, Transition } from "@headlessui/react";
import {
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { addMinutes, differenceInMinutes, format, parse } from "date-fns";
import { Fragment, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { GrCircleAlert } from "react-icons/gr";
import { useLocation, useNavigate } from "react-router";
import { IBookingCommentModal } from "../interface";
import NudgesContainer from "@/app/components/ui/Nudges/NudgesContainer";
import AddAddressModal from "../../HDCart/AddAddressModal";
import AddressListModal from "../../HDCart/AddressListModal";
import { useAddressUpdateOnSlotAPI } from "@/app/hooks/api_hooks/HDCart/useAddressUpdateOnSlotAPI";
import { useBookingSlotsListAPI } from "@/app/hooks/api_hooks/bookings/useBookingSlotsListAPI";
import { useBookedSlotsListAPI } from "@/app/hooks/api_hooks/bookings/useBookedSlotsListAPI";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import UnifiedCartProductSearchComponent from "@/app/components/routes/auth_routes/CartManagement/UnifiedCartProductSearchComponent";
import ChooseVaccineSection from "./components/ChooseVaccineSection";
import {
  buildVaccineCartItems,
  buildDiagnosticCartItems,
  DIAGNOSTIC_CART_ITEM_TYPE,
  getPreviewVaccineItems,
  resolveDiagnosticCartItems,
  isVaccinationServiceType,
  isVideoConsultationServiceType,
  isAddOnDiscountEnabled,
} from "@/app/utils/bookingServiceSelection";
import BillingSummaryFooter from "./components/BillingSummaryFooter";
import MembershipPlanCard from "./components/MembershipPlanCard";
import SurgeryOptionCard, { ISurgeryCardOption } from "./components/SurgeryOptionCard";
import SurgerySection, { ISurgerySectionCard } from "./components/SurgerySection";
import PurchaseOptionsSkeleton from "./components/PurchaseOptionsSkeleton";
import ServiceAddOnsSection from "./components/ServiceAddOnsSection";
import ServiceSummaryCard from "./components/ServiceSummaryCard";
import DiagnosticsSection from "./components/DiagnosticsSection";
import editicon from "@/assets/images/edit.svg";
// import RBACFlag from "@/app/components/core/RBACFlag";
import { HOME_SERVICE_ID } from "@/constants";

const SURGERY_SERVICE_TYPE_ID = "abf35f1e-f952-4f3e-9445-0c91c28361a2";

const PurchaseOptionsBlock = lazy(() => import("./PurchaseOptionsBlock"));

const AT_HOME_MEMBERSHIP_NAME_REGEX = /at\s*[-_]?\s*home|(?:^|[^a-z0-9])ahs(?:[^a-z0-9]|$)/i;
const isAtHomeMembershipName = (name?: string | null) => AT_HOME_MEMBERSHIP_NAME_REGEX.test(name ?? "");

const CommentSectionModalLoader = () => (
  <div className="animate-pulse space-y-4 p-4 sm:p-5">
    <div className="h-[180px] rounded-2xl bg-[#F3F5F8]" />
    <div className="h-[52px] rounded-xl bg-[#F3F5F8]" />
    <div className="h-[120px] rounded-xl bg-[#F3F5F8]" />
    <div className="h-[100px] rounded-xl bg-[#F3F5F8]" />
    <div className="h-[76px] rounded-xl bg-[#F3F5F8]" />
  </div>
);


export interface IBookingCommentsFormFields {
  reason?: string;
  coupon: string;
  couponId?: string;
  alternativeCoupon: string;
  membershipName: string;
  otherComment: string;
  lateBooking: boolean;
  lateBookingStartTime: string;
  vaccination: boolean;
  hygienePackage: boolean;
}

const CommentSectionModal: React.FC<IBookingCommentModal> = ({
  buttonName,
  activeButton,
  commentEdit,
  overBooking,
  onClick,
  renderType = "modal",
  openOnSignal,
  hideTrigger = false,
  isCommentModalEdit = false,
}) => {
  //Instantiations
  const { getService, getSubService } = useUtils();
  const isLateBookingCheckInProgress = useLoader("isLateBookingStatusCheckInProgress");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const slotsRouteId = getRouteKey("SLOTS", "id");
  const customerProfileRouteId = getRouteKey("CUSTOMER_PROFILE", "id");
  const { routeState: SRS } = useRouteInfo(slotsRouteId);
  const { routeState: CPRS } = useRouteInfo(customerProfileRouteId);
  const { status: hasVCBookingPermission } = useRBACFlag("ALLOW_VC_IN_BOOKINGS");

  const {
    status: lateBookingAvailability,
    execute: checkLateBookingAvailability,
    clearStatus,
  } = useLateBookingCheckAPI();

  //state to open Modal
  const cancelButtonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [nudgeAvailable, setNudgeAvailable] = useState(false);
  const [isAddressListModalOpen, setIsAddressListModalOpen] = useState(false);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const rescheduleBookingIdFromParams = new URLSearchParams(location.search).get("rbi");

  // Redux
  const {
    couponsData,
    filters,
    commentsData,

    rescheduledBooking,
    selectedSlotDetails,
    selectedSlotStaffDetails,
    selectedSlotsClinicDetails,
    overbookingFlag,
    rescheduleFlag,
    rescheduleBookingSummaryData,
    purchaseOptionsData,
    bookingCartPreviewData,
    selectedPurchaseServiceId,
    selectedPurchaseAddOnIds,
    selectedPurchaseVaccineIds,
    selectedVaccineCouponMap,
    selectedAddOnCouponMap,
    selectedPurchaseMembershipId,
    selectedPurchaseMembershipPayAtClinic,
    selectedMembershipCouponId,
    selectedMembershipCouponName,
    selectedSurgeryIds,
    selectedSurgeryCouponMap,
    selectedPurchaseDiagnosticIds,
    selectedPurchaseOtherTestIds,
    address,
    currentFormServiceId,
  } = SRS.state as ISlotsState;
  const selectedService = getService(currentFormServiceId || filters?.service_id);
  const isHomeServiceSelection = filters?.service_type_id === HOME_SERVICE_ID;
  const shouldHideHygiene = selectedService?.service_type_name?.toLowerCase()?.includes("grooming");
  const isSurgeryService = filters?.service_type_id === SURGERY_SERVICE_TYPE_ID;
  const { execute: fetchPurchaseOptions, isPurchaseOptionsInProgress } = useBookingPurchaseOptionsAPI();
  const { execute: fetchBookingSummary } = useBookingSummaryAPI();
  const couponsAPI = useBookingDiscountCouponListAPI();
  const fetchBookingsSlotsAPI = useBookingSlotsListAPI();
  const fetchBookedSlotsAPI = useBookedSlotsListAPI();
  const stringConstants = useStringConstants();

  const alert_category_list = ["Preferred Doctor"];

  // Fetch Selected Pet and Customer Details
  const { customerId, petId, customerDetails, petDetails, customerAddresses } =
    CPRS.state as ICustomerProfileState;

  /* Click-log analytics */
  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  const logBookingEvent = useCallback(
    (event_type: string, extra: Partial<IClickLogBody> = {}) => {
      logClick({
        event_type,
        scr_id: "BOOKING_PAGE",
        client_id: customerId,
        pet_id: petId,
        vms_user_id: vmsUserId,
        clinic_id: selectedSlotsClinicDetails?.clinic_id,
        clinic_name: selectedSlotsClinicDetails?.clinic_name,
        timestamp: Date.now(),
        ...extra,
      });
    },
    [logClick, customerId, petId, vmsUserId, selectedSlotsClinicDetails?.clinic_id, selectedSlotsClinicDetails?.clinic_name],
  );
  /* Fetch initial Data */
  useEffect(() => {
    if (!customerId || !petId || !filters?.sub_service_id || !selectedSlotsClinicDetails?.clinic_id) return;

    const couponParams = {
      user_id: customerId,
      pet_id: petId,
      sub_service_id: filters?.sub_service_id,
      clinic_id: selectedSlotsClinicDetails?.clinic_id,
    };

    couponsAPI(couponParams);
  }, [customerId, petId, filters?.sub_service_id, selectedSlotsClinicDetails?.clinic_id]);

  // Fetch Selected Pet and Customer Details

  const membershipOptionsFetchKeyRef = useRef<string | null>(null);
  const couponsItem = couponsData?.map((c) => ({
    title: c.coupon_name,
    id: c.coupon_id,
    state: { coupon_visibility: c.coupon_visibility },
  })) ?? [];

  const shouldRenderInline = renderType === "component";
  const shouldFetchPurchaseOptions = shouldRenderInline || open;
  const purchaseOptionsFetchKeyRef = useRef<string | null>(null);
  const [purchaseOptionsReadyKey, setPurchaseOptionsReadyKey] = useState<string | null>(null);
  const [hasModalBlinkDelayPassed, setHasModalBlinkDelayPassed] = useState(false);
  const purchasePreviewFetchKeyRef = useRef<string | null>(null);
  const { execute: fetchBookingCartPreview, isBookingCartPreviewInProgress, previewSurgeKey } = useBookingCartPreviewAPI();
  const {
    execute: fetchMembershipOptions,
    membershipOptions,
    isMembershipOptionsInProgress,
  } = useBookingMembershipOptionsAPI();

  const surgeryOptionsFetchKeyRef = useRef<string | null>(null);
  const {
    execute: fetchSurgeryOptions,
    surgeryOptions,
    isSurgeryOptionsInProgress,
  } = useBookingSurgeryOptionsAPI();

  const membershipItems = membershipOptions.map(({ id, name, price, membership_type }) => ({
    id,
    title: name,
    membership_type,
    state: {
      membershipName: name,
      rightLabel: <span>₹{price}</span>,
    },
  }));

  const ahsMembershipItems = membershipItems.filter((membership) => isAtHomeMembershipName(membership.title));
  const doggyWorldMembershipItems = membershipItems.filter(
    (membership) => !isAtHomeMembershipName(membership.title) && membership.membership_type === "doggy_world",
  );
  const clinicMembershipItems = membershipItems.filter(
    (membership) => !isAtHomeMembershipName(membership.title) && membership.membership_type !== "doggy_world",
  );

  const surgeryItems = useMemo(
    () =>
      surgeryOptions.map(({ id, name, price }) => ({
        id,
        title: name,
        state: {
          rightLabel: <span>₹{Math.round(Number(price ?? 0))}</span>,
        },
      })),
    [surgeryOptions],
  );

  // Visit time surge is priced for: the slot, or an overbooking's own time.
  const surgeTime = useMemo(
    () => surgeSlotPayload(selectedSlotDetails, filters?.booking_date),
    [selectedSlotDetails, filters?.booking_date],
  );
  const selectedSurgeryItems = useMemo(
    () => surgeryItems.filter((item) => (selectedSurgeryIds ?? []).includes(item.id)),
    [surgeryItems, selectedSurgeryIds],
  );

  const selectedServicePurchaseOption = useMemo(
    () =>
      purchaseOptionsData?.services?.find((service) => service.id === selectedPurchaseServiceId) ||
      purchaseOptionsData?.services?.[0] ||
      null,
    [purchaseOptionsData, selectedPurchaseServiceId],
  );
  const selectedMembershipOption = useMemo(
    () => purchaseOptionsData?.memberships?.find((membership) => membership.id === selectedPurchaseMembershipId) || null,
    [purchaseOptionsData, selectedPurchaseMembershipId],
  );
  const selectedSummaryMembershipItem = useMemo(
    () =>
      (rescheduleBookingSummaryData?.addons?.memberships ?? []).find(
        (membership) => membership.id === selectedPurchaseMembershipId,
      ) ?? null,
    [rescheduleBookingSummaryData, selectedPurchaseMembershipId],
  );
  const isSelectedMembershipBilled = String(selectedSummaryMembershipItem?.billing_status ?? "").toUpperCase() === "BILLED";

  const hasInitializedRescheduleRef = useRef<string | null>(null);

  useEffect(() => {
    if (isCommentModalEdit) return;
    if (!rescheduleFlag || !rescheduleBookingSummaryData || !purchaseOptionsData) return;
    if (hasInitializedRescheduleRef.current === rescheduleBookingIdFromParams) return;

    const serviceOptionIds = new Set((purchaseOptionsData?.services ?? []).map((service) => service.id));

    const summaryServiceCandidates = [
      rescheduleBookingSummaryData?.service?.sub_service_id,
      rescheduleBookingSummaryData?.service?.service_id,
    ].filter(Boolean);
    const summaryAddOnIds = [
      ...new Set(
        [
          ...(rescheduleBookingSummaryData?.addons?.add_on_services ?? []),
          ...(rescheduleBookingSummaryData?.addons?.add_on_boms ?? []),
        ].map((addOn) => addOn.id),
      ),
    ];
    const summaryMembershipId = rescheduleBookingSummaryData?.addons?.memberships?.[0]?.id ?? null;
    const summaryVaccineIds = [
      ...new Set(
        (rescheduleBookingSummaryData?.addons?.vaccinations ?? [])
          .filter((vaccine) => vaccine.qty === 1 || (vaccine.qty ?? 1) >= 1)
          .map((vaccine) => vaccine.vaccine_id || vaccine.id),
      ),
    ];
    const summaryVaccineCouponMap: Record<string, string> = {};
    (rescheduleBookingSummaryData?.addons?.vaccinations ?? []).forEach((vaccine) => {
      const vId = vaccine.vaccine_id || vaccine.id;
      if (vaccine.coupon_id) {
        summaryVaccineCouponMap[vId] = vaccine.coupon_id;
      }
    });

    const matchedServiceId =
      summaryServiceCandidates.find((serviceId) => serviceOptionIds.has(serviceId)) ||
      purchaseOptionsData?.services?.[0]?.id ||
      null;

    const matchedAddOnIds = summaryAddOnIds;
    const matchedMembershipId = summaryMembershipId ?? null;

    const existingAddOnIds = [...(selectedPurchaseAddOnIds ?? [])].sort();
    const nextAddOnIds = [...matchedAddOnIds].sort();
    const areAddOnIdsSame =
      existingAddOnIds.length === nextAddOnIds.length &&
      existingAddOnIds.every((id, index) => id === nextAddOnIds[index]);

    const existingVaccineIds = [...(selectedPurchaseVaccineIds ?? [])].sort();
    const nextVaccineIds = [...summaryVaccineIds].sort();
    const areVaccineIdsSame =
      existingVaccineIds.length === nextVaccineIds.length &&
      existingVaccineIds.every((id, index) => id === nextVaccineIds[index]);

    if (selectedPurchaseServiceId !== matchedServiceId) {
      dispatch(setSelectedPurchaseServiceId(matchedServiceId));
    }
    if (!areAddOnIdsSame) {
      dispatch(setSelectedPurchaseAddOnIds({ data: matchedAddOnIds }));
    }
    if ((selectedPurchaseMembershipId ?? null) !== matchedMembershipId) {
      dispatch(setSelectedPurchaseMembershipId(matchedMembershipId));
    }
    if (!areVaccineIdsSame) {
      dispatch(setSelectedPurchaseVaccineIds({ data: summaryVaccineIds }));
    }
    if (JSON.stringify(selectedVaccineCouponMap ?? {}) !== JSON.stringify(summaryVaccineCouponMap)) {
      dispatch(setSelectedVaccineCouponMap({ data: summaryVaccineCouponMap }));
    }

    hasInitializedRescheduleRef.current = rescheduleBookingIdFromParams;
  }, [
    dispatch,
    isCommentModalEdit,
    purchaseOptionsData,
    rescheduleFlag,
    rescheduleBookingSummaryData,
    rescheduleBookingIdFromParams,
    selectedPurchaseAddOnIds,
    selectedPurchaseServiceId,
    selectedPurchaseMembershipId,
    selectedPurchaseVaccineIds,
    selectedVaccineCouponMap,
  ]);

  const hasInitializedOtherTestsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isCommentModalEdit || !purchaseOptionsData || !bookingCartPreviewData) return;
    const initKey = rescheduleBookingIdFromParams ?? "new-booking";
    if (hasInitializedOtherTestsRef.current === initKey) return;
    hasInitializedOtherTestsRef.current = initKey;
    if ((selectedPurchaseOtherTestIds ?? []).length > 0) return;

    const otherTestOptionIds = new Set((purchaseOptionsData.other_diagnostic_test ?? []).map((item) => item.id));
    const restoredIds = [
      ...new Set(
        [
          ...(bookingCartPreviewData.cart_items ?? []),
          ...(rescheduleFlag ? rescheduleBookingSummaryData?.addons?.diagnostics ?? [] : []),
        ]
          .map((item) => item.id)
          .filter((id) => otherTestOptionIds.has(id)),
      ),
    ];
    if (restoredIds.length > 0) {
      dispatch(setSelectedPurchaseOtherTestIds(restoredIds));
    }
  }, [
    dispatch,
    isCommentModalEdit,
    purchaseOptionsData,
    bookingCartPreviewData,
    rescheduleFlag,
    rescheduleBookingSummaryData,
    rescheduleBookingIdFromParams,
    selectedPurchaseOtherTestIds,
  ]);

  const updatedAddress = useAddressUpdateOnSlotAPI();
  const isRescheduledVCBooking = rescheduledBooking?.subservice_id;
  const isVCFilterSelected = filters?.sub_service_id;
  const isRescheduledFlow = Boolean(rescheduledBooking?.service_id);
  const isVCSelectionInvalid = Boolean(isRescheduledFlow && ((isRescheduledVCBooking && !isVCFilterSelected) || (!isRescheduledVCBooking && isVCFilterSelected)));
  const vcValidationMessage = isRescheduledVCBooking
    ? "Reschedule VC bookings to VC only."
    : "Only VC bookings can be rescheduled to VC.";

  // Check RBAC permission for VC bookings (new bookings only)
  const isSelectedServiceVC = selectedService?.service_type_name?.toLowerCase()?.includes("video");
  const isNewVCBooking = !rescheduleFlag && isSelectedServiceVC;
  const isVCBookingNotAllowed = isNewVCBooking && !hasVCBookingPermission;

  const onSubmit = (data: IBookingCommentsFormFields) => {
    if (isVCSelectionInvalid) {
      return;
    }
    // Block vaccination preview until a vaccine is selected.
    if (isVaccinationServiceType(filters?.service_id) && (selectedPurchaseVaccineIds ?? []).length === 0) {
      return;
    }
    if (isDiagnosticSelectionMissing || isSurgerySelectionMissing) {
      return;
    }
    formHook.trigger().then((isValid: boolean) => {
      if (isValid) {
        if (!data.lateBooking) {
          dispatch(setLateBookingFlag(false));
          dispatch(setLateBookingStartTime(null));
        } else {
          dispatch(setLateBookingFlag(true));
          dispatch(setLateBookingStartTime(convert12HrsTo24Hrs(data.lateBookingStartTime)));
        }
        /* Perform Next Actions Here */
        const pattern = /[a-zA-Z0-9\s%||]+/g;
        const extractedComment = data?.otherComment || "";

        const updatedData: IBookingCommentsFormFields = {
          ...data,
          reason: data?.reason || commentsData?.reason,
          couponId: data?.couponId || selectedCouponId || undefined,
          otherComment: extractedComment,
        };
        dispatch(
          setSlotsComments({
            data: updatedData,
          }),
        );
        const submitSelectedVaccineSet = new Set(selectedPurchaseVaccineIds ?? []);
        const allVaccines = previewVaccineItems.map((item) => ({
          vaccine_id: item.id,
          vaccine_name: item.name,
          price: item.price,
          final_price: item.final_price,
          discount_percent: item.discount_percent,
          coupon_id: vaccineCouponMap?.[item.id] ?? null,
          is_selected: submitSelectedVaccineSet.has(item.id),
        }));
        logBookingEvent("COMMENT_FORM_SUBMIT", {
          cta_text: commentEdit ? "Update" : "Continue to Preview",
          checkbox_text: data.lateBooking ? "Late Booking" : "Normal Booking",
          search_item: data?.reason || undefined,
          vaccines: allVaccines,
          vaccine_count: allVaccines.length,
          filter_values: {
            coupon: updatedData?.coupon || null,
            coupon_id: updatedData?.couponId || null,
            membership_id: selectedPurchaseMembershipId || null,
            add_on_ids: selectedPurchaseAddOnIds ?? [],
            vaccine_ids: selectedPurchaseVaccineIds ?? [],
            vaccination_diary: Boolean(updatedData?.vaccination),
            service_id: filters?.service_id || null,
          },
        });
        setOpen(false);
        if (!commentEdit) {
          navigate(`/app/slots/booking-confirmation/${customerId}/${petId}${location.search}`);
        }
      }
    });
    updatedAddress(customerId, address);
  };

  const formHook = useForm<IBookingCommentsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  const selectedCouponLabel = formHook.watch("coupon");
  const selectedCouponId =
    !selectedCouponLabel
      ? undefined
      : formHook.watch("couponId") ||
      couponsData?.find((coupon) => coupon?.coupon_name === selectedCouponLabel)?.coupon_id ||
      commentsData?.couponId ||
      undefined;
  const visibleButton = () => {
    return true;
  };

  useEffect(() => {
    if (!commentEdit) return;
    if (!shouldRenderInline && !open) return;
    if (!filters) return;
    formHook.setValue("vaccination", commentsData?.vaccination);
    // formHook.setValue("alternativeCoupon", commentsData?.alternativeCoupon ?? "");
    formHook.setValue("reason", commentsData?.reason);
    formHook.setValue("coupon", commentsData?.coupon);
    formHook.setValue("couponId", commentsData?.couponId);
    formHook.setValue("hygienePackage", commentsData?.hygienePackage);
    formHook.setValue("otherComment", commentsData?.otherComment ?? "");
    formHook.setValue("membershipName", commentsData?.membershipName ?? "");
  }, [commentEdit, open, shouldRenderInline, filters, commentsData, formHook]);

  useEffect(() => {
    if (!shouldRenderInline && !open) return;
    const defaultCouponId = selectedServicePurchaseOption?.coupon_id;
    if (!defaultCouponId) return;

    const existingCoupon = formHook.getValues("coupon");
    const existingCouponId = formHook.getValues("couponId");
    if (existingCoupon || existingCouponId) return;

    const item = couponsItem?.find((couponItem) => couponItem.id === defaultCouponId);
    if (!item) return;

    formHook.setValue("coupon", item.title);
    formHook.setValue("couponId", item.id);
  }, [open, shouldRenderInline, selectedServicePurchaseOption?.coupon_id, couponsItem, formHook]);

  useEffect(() => {
    if (!open && !shouldRenderInline) {
      clearStatus();
    }
  }, [open, shouldRenderInline]);

  useEffect(() => {
    if (shouldRenderInline) return;
    if (!openOnSignal || openOnSignal <= 0) return;
    setNudgeAvailable(false);
    setOpen(true);
  }, [openOnSignal, shouldRenderInline]);

  useEffect(() => {
    if (!shouldFetchPurchaseOptions) return;
    const subServiceId = filters?.sub_service_id;
    const clinicId = selectedSlotsClinicDetails?.clinic_id;
    if (!subServiceId || !clinicId || !customerId) return;

    const fetchKey = `${subServiceId}-${clinicId}-${customerId}`;
    if (isCommentModalEdit && purchaseOptionsData && purchaseOptionsFetchKeyRef.current === null) {
      purchaseOptionsFetchKeyRef.current = fetchKey;
      setPurchaseOptionsReadyKey(fetchKey);
      return;
    }
    if (purchaseOptionsFetchKeyRef.current === fetchKey) return;
    purchaseOptionsFetchKeyRef.current = fetchKey;
    setPurchaseOptionsReadyKey(null);

    fetchPurchaseOptions({
      sub_service_id: subServiceId,
      clinic_id: clinicId,
      user_id: customerId,
      pet_id: petId,
    }).finally(() => {
      if (purchaseOptionsFetchKeyRef.current === fetchKey) {
        setPurchaseOptionsReadyKey(fetchKey);
      }
    });
  }, [
    shouldFetchPurchaseOptions,
    filters,
    selectedSlotsClinicDetails?.clinic_id,
    customerId,
    fetchPurchaseOptions,
    isCommentModalEdit,
    purchaseOptionsData,
    petId,
  ]);

  useEffect(() => {
    if (!shouldFetchPurchaseOptions) {
      membershipOptionsFetchKeyRef.current = null;
      return;
    }
    const clinicId = selectedSlotsClinicDetails?.clinic_id;
    if (!clinicId || !petId) return;

    const fetchKey = `${clinicId}:${petId}:${filters?.service_id ?? ""}`;
    if (membershipOptionsFetchKeyRef.current === fetchKey) return;
    membershipOptionsFetchKeyRef.current = fetchKey;
    fetchMembershipOptions(clinicId, petId, filters?.service_id);
  }, [
    shouldFetchPurchaseOptions,
    selectedSlotsClinicDetails?.clinic_id,
    petId,
    filters?.service_id,
    fetchMembershipOptions,
  ]);

  useEffect(() => {
    if (!shouldFetchPurchaseOptions || !isSurgeryService) {
      surgeryOptionsFetchKeyRef.current = null;
      return;
    }
    const clinicId = selectedSlotsClinicDetails?.clinic_id;
    if (!clinicId || !petId) return;

    const fetchKey = `${clinicId}:${petId}:${filters?.service_id ?? ""}`;
    if (surgeryOptionsFetchKeyRef.current === fetchKey) return;
    surgeryOptionsFetchKeyRef.current = fetchKey;
    fetchSurgeryOptions(clinicId, petId, filters?.service_id);
  }, [
    shouldFetchPurchaseOptions,
    isSurgeryService,
    selectedSlotsClinicDetails?.clinic_id,
    petId,
    filters?.service_id,
    fetchSurgeryOptions,
  ]);

  const requestBookingCartPreview = useCallback(
    (
      forceRefresh = false,
      couponIdOverride?: string | null,
      membershipCouponIdOverride?: string | null,
      membershipCouponNameOverride?: string | null,
      surgeryCouponOverride?: { surgeryId: string; couponId: string | null; couponName?: string | null },
      addOnCouponOverride?: { addOnId: string; couponId: string | null }
    ) => {
      const clinicId = selectedSlotsClinicDetails?.clinic_id;
      const subServiceId = filters?.sub_service_id;
      if (!clinicId || !customerId || !subServiceId) return;

      // The option first: on reschedule to another service, the selected id can still be the old booking's.
      const serviceCartItemId = selectedServicePurchaseOption?.id || selectedPurchaseServiceId || filters?.sub_service_id;
      if (!serviceCartItemId) return;

      const effectiveCouponId =
        couponIdOverride === undefined ? selectedCouponId : couponIdOverride || undefined;
      const effectiveMembershipCouponId =
        membershipCouponIdOverride === undefined ? selectedMembershipCouponId : membershipCouponIdOverride || undefined;
      const effectiveMembershipCouponName =
        membershipCouponNameOverride === undefined ? selectedMembershipCouponName : membershipCouponNameOverride || undefined;
      const effectiveSurgeryCouponMap = { ...(selectedSurgeryCouponMap ?? {}) };
      if (surgeryCouponOverride) {
        if (surgeryCouponOverride.couponId) {
          effectiveSurgeryCouponMap[surgeryCouponOverride.surgeryId] = {
            couponId: surgeryCouponOverride.couponId,
            ...(surgeryCouponOverride.couponName ? { couponName: surgeryCouponOverride.couponName } : {}),
          };
        } else {
          delete effectiveSurgeryCouponMap[surgeryCouponOverride.surgeryId];
        }
      }
      const surgeryCartItems = isSurgeryService
        ? surgeryOptions
            .filter((surgery) => (selectedSurgeryIds ?? []).includes(surgery.id))
            .map((surgery) => {
              const surgeryCoupon = effectiveSurgeryCouponMap[surgery.id];
              return {
                type: surgery.type,
                id: surgery.id,
                qty: 1,
                ...(surgeryCoupon?.couponId ? { coupon_id: surgeryCoupon.couponId } : {}),
                ...(surgeryCoupon?.couponName ? { coupon_name: surgeryCoupon.couponName } : {}),
              };
            })
        : [];

      const selectedAddOnIds = [...(selectedPurchaseAddOnIds ?? [])].sort();
      const effectiveAddOnCouponMap = { ...(selectedAddOnCouponMap ?? {}) };
      if (addOnCouponOverride) {
        if (addOnCouponOverride.couponId) {
          effectiveAddOnCouponMap[addOnCouponOverride.addOnId] = addOnCouponOverride.couponId;
        } else {
          delete effectiveAddOnCouponMap[addOnCouponOverride.addOnId];
        }
      }
      const selectedAddOnCartItems = (purchaseOptionsData?.service_add_ons ?? [])
        .filter((addOn) => selectedAddOnIds.includes(addOn.id))
        .map((addOn) => {
          const addOnCouponId = effectiveAddOnCouponMap[addOn.id];
          return {
            type: addOn?.type,
            id: addOn?.id,
            qty: addOn?.max_qty,
            ...(addOnCouponId ? { coupon_id: addOnCouponId } : {}),
          };
        });
      const serviceCartItem = {
        type: "service",
        id: serviceCartItemId,
        qty: 1,
        ...(effectiveCouponId ? { coupon_id: effectiveCouponId } : {}),
      };
      // Echo server-derived vaccine catalog back with qty per selection (qty 0 = suggestion, 1 = selected)
      const vaccineCartItems = buildVaccineCartItems({
        previewVaccineItems: getPreviewVaccineItems(bookingCartPreviewData?.cart_items),
        selectedVaccineIds: selectedPurchaseVaccineIds ?? [],
        vaccineCouponMap: selectedVaccineCouponMap,
      });
      
      const purchaseDiagnosticIds = new Set(
        [...(purchaseOptionsData?.diagnostics ?? []), ...(purchaseOptionsData?.other_diagnostic_test ?? [])].map(
          (item) => item.id,
        ),
      );
      const summaryDiagnosticIds = new Set(
        (rescheduleFlag ? rescheduleBookingSummaryData?.addons?.diagnostics ?? [] : []).map((item) => item.id),
      );
      const echoedDiagnosticCartItems = buildDiagnosticCartItems({
        previewCartItems: bookingCartPreviewData?.cart_items,
        summaryData: rescheduleFlag ? rescheduleBookingSummaryData : null,
      })
        .filter((item) => !purchaseDiagnosticIds.has(item.id) || summaryDiagnosticIds.has(item.id))
        .filter((item) => !(selectedPurchaseOtherTestIds ?? []).includes(item.id));
      const echoedDiagnosticIds = new Set(echoedDiagnosticCartItems.map((item) => item.id));
      const diagnosticCartItems = [
        ...echoedDiagnosticCartItems,
        ...(purchaseOptionsData?.diagnostics ?? [])
          .filter((item) => (selectedPurchaseDiagnosticIds ?? []).includes(item.id) && !echoedDiagnosticIds.has(item.id))
          .map((item) => ({ type: DIAGNOSTIC_CART_ITEM_TYPE, id: item.id, qty: 1 })),
      ];
      const otherTestCartItems = (purchaseOptionsData?.other_diagnostic_test ?? [])
        .filter((item) => (selectedPurchaseOtherTestIds ?? []).includes(item.id))
        .map((item) => ({ type: "other_diagnostic_test", id: item.id, qty: 1 }));
      const cartItems = [
        serviceCartItem,
        ...selectedAddOnCartItems,
        ...vaccineCartItems,
        ...diagnosticCartItems,
        ...otherTestCartItems,
        ...(selectedPurchaseMembershipId && !isSelectedMembershipBilled
          ? [
            {
              type: "membership" as const,
              id: selectedPurchaseMembershipId,
              qty: 1,
              pay_at_clinic: Boolean(selectedPurchaseMembershipPayAtClinic),
              ...(effectiveMembershipCouponId ? { coupon_id: effectiveMembershipCouponId } : {}),
              ...(effectiveMembershipCouponName ? { coupon_name: effectiveMembershipCouponName } : {}),
            },
          ]
          : []),
        ...surgeryCartItems,
      ];

      const fetchKey = JSON.stringify({
        clinic_id: clinicId,
        user_id: customerId,
        sub_service_id: subServiceId,
        cart_items: cartItems,
        configuration_id: selectedSlotDetails?.configuration_id,
        surge: surgeKey(surgeTime),
      });

      if (!forceRefresh) {
        if (isCommentModalEdit && bookingCartPreviewData && purchasePreviewFetchKeyRef.current === null) {
          purchasePreviewFetchKeyRef.current = fetchKey;
          return;
        }
        if (purchasePreviewFetchKeyRef.current === fetchKey) return;
      }

      purchasePreviewFetchKeyRef.current = fetchKey;
      fetchBookingCartPreview({
        user_id: customerId,
        pet_id: petId,
        clinic_id: clinicId,
        sub_service_id: subServiceId,
        cart_items: cartItems,
        configuration_id: selectedSlotDetails?.configuration_id,
        ...surgeTime,
        ...(rescheduleFlag && rescheduleBookingIdFromParams
          ? {
            is_reschedule: true,
            booking_id: rescheduleBookingIdFromParams,
          }
          : {}),
      });
    },
    [
      selectedSlotsClinicDetails?.clinic_id,
      filters?.sub_service_id,
      customerId,
      selectedPurchaseServiceId,
      selectedServicePurchaseOption?.id,
      selectedPurchaseAddOnIds,
      selectedPurchaseVaccineIds,
      selectedVaccineCouponMap,
      selectedAddOnCouponMap,
      purchaseOptionsData?.service_add_ons,
      purchaseOptionsData?.diagnostics,
      selectedPurchaseDiagnosticIds,
      purchaseOptionsData?.other_diagnostic_test,
      selectedPurchaseOtherTestIds,
      selectedPurchaseMembershipId,
      isSelectedMembershipBilled,
      selectedPurchaseMembershipPayAtClinic,
      selectedCouponId,
      selectedMembershipCouponId,
      selectedMembershipCouponName,
      isSurgeryService,
      surgeryOptions,
      selectedSurgeryIds,
      selectedSurgeryCouponMap,
      selectedSlotDetails?.configuration_id,
      // A new visit time can change surge prices; without it the old time's preview is reused.
      surgeTime,
      isCommentModalEdit,
      bookingCartPreviewData,
      rescheduleBookingSummaryData,
      fetchBookingCartPreview,
      petId,
      rescheduleFlag,
      rescheduleBookingIdFromParams,
    ],
  );

  useEffect(() => {
    if (!shouldFetchPurchaseOptions) return;
    const clinicId = selectedSlotsClinicDetails?.clinic_id;
    const subServiceId = filters?.sub_service_id;
    if (!clinicId || !customerId || !subServiceId) return;
    const purchaseOptionsKey = `${subServiceId}-${clinicId}-${customerId}`;
    if (purchaseOptionsReadyKey !== purchaseOptionsKey) return;
    requestBookingCartPreview();
  }, [
    shouldFetchPurchaseOptions,
    selectedSlotsClinicDetails?.clinic_id,
    customerId,
    filters?.sub_service_id,
    purchaseOptionsReadyKey,
    requestBookingCartPreview,
  ]);

  useEffect(() => {
    if (!selectedPurchaseMembershipId) return;
    if (selectedMembershipOption?.name) {
      formHook.setValue("membershipName", selectedMembershipOption.name);
      return;
    }
    if (selectedSummaryMembershipItem?.name) {
      formHook.setValue("membershipName", selectedSummaryMembershipItem.name);
    }
  }, [selectedMembershipOption, selectedSummaryMembershipItem, selectedPurchaseMembershipId, formHook]);

  useAfterFirstRender(() => {
    if (!open && !shouldRenderInline) return;
    if (!formHook.getValues("lateBookingStartTime")) return;
    checkLateBookingAvailability({
      clinic_id: selectedSlotsClinicDetails?.clinic_id,
      service_id: filters?.service_id,
      staff_id: selectedSlotStaffDetails?.staff_id,
      start_time: convert12HrsTo24Hrs(formHook.getValues("lateBookingStartTime")),
      sub_service_id: filters?.sub_service_id,
    });
  }, [formHook.watch("lateBookingStartTime"), shouldRenderInline]);

  const convert24HrsTo12Hrs = (time24Hrs) => {
    if (!time24Hrs) return;
    return format(parse(time24Hrs, "HH:mm:ss", new Date()), "h:mm a");
  };

  const convert12HrsTo24Hrs = (time12Hrs: string) => {
    return format(parse(time12Hrs, "h:mm a", new Date()), "HH:mm:ss");
  };

  const calculateLateBookingStartTime = () => {
    const startTime = selectedSlotDetails?.start_time;
    const endTime = selectedSlotDetails?.end_time;

    const startDate = new Date(`2022-01-01T${startTime}`);
    const endDate = new Date(`2022-01-01T${endTime}`);

    const diffInMinutes = differenceInMinutes(endDate, startDate);

    const intervals = [];
    for (let i = 0; i < diffInMinutes; i += 5) {
      intervals.push(format(addMinutes(startDate, i), "h:mm a"));
    }
    // Remove the 0th index
    intervals.splice(0, 3);
    return intervals?.map((i) => {
      return { id: i, title: i };
    });
  };

  const lateBookingIntervalItems = calculateLateBookingStartTime();

  useEffect(() => {
    const cleanCommentsArray = (comments) => {
      return comments?.map((comment) => comment.trim());
    };

    if (rescheduledBooking) {
      const CoomentData = rescheduledBooking?.appointment_note;

      const split_item = rescheduledBooking?.appointment_note?.startsWith("<ol>")
        ? CoomentData?.split("</li>")
        : CoomentData?.split("||");

      let modifiedArray = rescheduledBooking?.appointment_note?.startsWith("<ol>")
        ? split_item?.map((item) => item?.replace(/<li>|<\/li>|<\/?ol>/g, ""))
        : cleanCommentsArray(split_item?.map((item) => item?.replace(/<li>|<\/li>|<\/?ol>/g, "")));

      if (rescheduledBooking?.reason) {
        formHook.setValue("reason", rescheduledBooking.reason);
      }
      modifiedArray = modifiedArray?.filter((item) => !item?.startsWith("Reason For Appointment:"));

      //final price
      const Final_price = modifiedArray?.reverse().find((item) => item?.includes("Discount Coupon:"));
      const final_price = Final_price?.split(":")[1];

      if (Final_price) {
        formHook.setValue("coupon", final_price);
        modifiedArray = modifiedArray.filter((item) => item !== Final_price);
      }

      // Alternative Discount
      const alternative_discount = modifiedArray
        ?.reverse()
        .find((item) => item?.includes("Alternative Service Discount:"));
      const alternative_discount_percent = alternative_discount?.split(":")[1];

      if (alternative_discount_percent) {
        formHook.setValue("alternativeCoupon", alternative_discount_percent);
        modifiedArray = modifiedArray.filter((item) => item !== alternative_discount);
      }

      //Membership
      const membership = modifiedArray?.reverse().find((item) => item?.includes("Membership Pitched to Client:"));
      const membership_name = membership?.split(":")[1];

      if (membership_name) {
        formHook.setValue("membershipName", membership_name);
        modifiedArray = modifiedArray.filter((item) => item !== membership);
      }

      const vaccination = modifiedArray
        ?.reverse()
        .find((item) => item?.includes("Client will bring Vaccination Diary:"));
      const vaccination_flage = vaccination?.split(":")[1];

      if (vaccination_flage === "Yes") {
        formHook.setValue("vaccination", true);
        modifiedArray = modifiedArray.filter((item) => item !== vaccination);
      }

      // Hygin package
      const hygin = modifiedArray?.reverse().find((item) => item?.includes("Hygiene package add-on required:"));
      const hygin_flage = hygin?.split(":")[1];

      if (hygin_flage === "Yes") {
        formHook.setValue("hygienePackage", true);
        modifiedArray = modifiedArray.filter((item) => item !== hygin);
      }

      //other text
      const singleString = modifiedArray?.join(" • ");

      const cleanedArray = modifiedArray
        ?.filter((item) => item.trim() !== "")
        ?.map((item) => item.replace(/^Other:/, "").replace(/<\/?b>/g, ""));

      if (cleanedArray) {
        formHook.setValue("otherComment", cleanedArray?.join(" • "));
      }
    }
  }, []);

  //Function to open modal for Nudges
  const handelClick = () => {
    logBookingEvent("PROCEED_TO_COMMENT_MODAL_CLICK", {
      staff_id: selectedSlotStaffDetails?.staff_id,
      cta_text: buttonName,
    });
    const hasSelectedAddress = Boolean(
      (address as any)?.state?.formattedAddress || (address as any)?.title,
    );
    if (isHomeServiceSelection && !hasSelectedAddress) {
      setIsAddressListModalOpen(true);
      return;
    }
    if (rescheduleBookingIdFromParams) {
      fetchBookingSummary(rescheduleBookingIdFromParams);
    }
    if (selectedSlotStaffDetails?.preferred_staff) {
      setOpen(true);
      setNudgeAvailable(false);
    } else {
      setNudgeAvailable(true);
    }
  };

  const setIsNotPreferedDoctorNudgeOpen = (isNudgeOpen: boolean, isClick: any) => {
    if (isNudgeOpen) {
      setNudgeAvailable(true);
      setOpen(false);
    } else if (isClick === "CLICK") {
      setOpen(false);
      setNudgeAvailable(false);
    } else {
      setOpen(true);
      setNudgeAvailable(false);
    }
  };

  const bookingDateLabel = (() => {
    if (!filters?.booking_date) return "--";
    const bookingDate = new Date(filters.booking_date);
    if (Number.isNaN(bookingDate.getTime())) return filters.booking_date;
    return format(bookingDate, "EEE dd, MMM");
  })();
  const queryParams = new URLSearchParams(location.search);
  const selectedAddressFromState = (address as any)?.state?.formattedAddress || (address as any)?.title || null;
  const selectedVisitAddress =
    selectedAddressFromState ||
    queryParams.get("visit_address") ||
    queryParams.get("address") ||
    "NA";
  const selectedClinicName =
    selectedSlotsClinicDetails?.clinic_name ||
    queryParams.get("clinic_name") ||
    queryParams.get("clinic") ||
    "NA";
  const estimatedTravelTimeLabel = queryParams.get("travel_time") || "~18 min.";
  const availableSlotsAddressChangeUrl = (() => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("open_address_list", "true");
    return `${getRouteKey("AVAILABLE_SLOTS", "url")}${nextParams.toString() ? `?${nextParams.toString()}` : ""}`;
  })();
  const handleEditVisitAddress = () => {
    logBookingEvent("EDIT_VISIT_ADDRESS_CLICK");
    setOpen(false);
    navigate(availableSlotsAddressChangeUrl);
  };
  useEffect(() => {
    if (!petDetails) return;
    dispatch(
      setPetDetails({
        petDetails: {
          ...petDetails,
          weight: bookingCartPreviewData?.pet_weight,
        },
      }),
    );
  }, [bookingCartPreviewData?.pet_weight, dispatch, petDetails?.weight]);
  const formatAmount = (amount: number) => `\u20b9${Number(amount || 0).toLocaleString("en-IN")}`;
  const previewCartItems = bookingCartPreviewData?.cart_items ?? [];
  const previewServiceItem = previewCartItems.find((item) => item.type === "service") ?? null;
  const summaryService = rescheduleFlag ? rescheduleBookingSummaryData?.service : null;
  const serviceOfferCallout = summaryService?.gift_id ? summaryService.offer_callout : null;
  const previewMembershipItem =
    selectedPurchaseMembershipId
      ? previewCartItems.find((item) => item.type === "membership" && item.id === selectedPurchaseMembershipId) ?? null : null;
  const previewAddOnItemsMap = new Map(
    previewCartItems
      .filter(
        (item) =>
          item.type === "service_add_ons" ||
          item.type === "add_on" ||
          item.type === "add_on_service" ||
          item.type === "add_on_bom",
      )
      .map((item) => [item.id, item]),
  );
  const selectedServicePrice = Number(
    previewServiceItem?.final_price ?? selectedServicePurchaseOption?.final_price ?? selectedSlotDetails?.booking_fee ?? 0,
  );
  const selectedServiceOriginalPrice = Number(
    previewServiceItem?.price ?? selectedServicePurchaseOption?.price ?? selectedServicePrice,
  );
  const selectedServiceDiscount = previewServiceItem?.discount_percent ?? 0;
  const vaccinationSelected = Boolean(formHook.watch("vaccination"));
  const selectedMembershipNameValue = formHook.watch("membershipName");
  const selectedMembershipDropdownItem = useMemo(() => {
    const selectedMembershipFromOptions = membershipItems?.find(
      (membership) => membership.id === selectedPurchaseMembershipId,
    );
    if (selectedMembershipFromOptions) {
      return selectedMembershipFromOptions;
    }
    if (selectedPurchaseMembershipId && selectedMembershipNameValue) {
      return {
        id: selectedPurchaseMembershipId,
        title: selectedMembershipNameValue,
      };
    }
    return undefined;
  }, [membershipItems, selectedPurchaseMembershipId, selectedMembershipNameValue]);
  const selectedMembershipCategory = isAtHomeMembershipName(selectedMembershipDropdownItem?.title)
    ? "ahs"
    : selectedMembershipDropdownItem?.membership_type === "doggy_world"
      ? "doggyWorld"
      : "clinic";
  const membershipDropdowns = [
    {
      key: "ahs",
      label: "AHS Membership Plans",
      placeholder: "Select AHS Membership",
      emptyLabel: "No AHS memberships found",
      selectItems: ahsMembershipItems,
      defaultSelected: selectedMembershipCategory === "ahs" ? selectedMembershipDropdownItem : undefined,
      wrapperClassName: "mb-4",
    },
    {
      key: "doggyWorld",
      label: "Doggy World Membership Plans",
      placeholder: "Select Doggy World Membership",
      emptyLabel: "No Doggy World memberships found",
      selectItems: doggyWorldMembershipItems,
      defaultSelected: selectedMembershipCategory === "doggyWorld" ? selectedMembershipDropdownItem : undefined,
      wrapperClassName: "mb-4",
    },
    {
      key: "clinic",
      label: "Membership Plans",
      placeholder: "Select Membership",
      emptyLabel: "No memberships found",
      selectItems: clinicMembershipItems,
      defaultSelected: selectedMembershipCategory === "clinic" ? selectedMembershipDropdownItem : undefined,
      wrapperClassName: "",
    },
  ];
  const surgeryCards: ISurgerySectionCard[] = surgeryOptions
    .filter((surgery) => (selectedSurgeryIds ?? []).includes(surgery.id))
    .map((surgery) => {
      const previewItem = previewCartItems.find((item) => item.id === surgery.id) ?? null;
      return {
        option: previewItem
          ? {
              id: previewItem.id,
              name: previewItem.name,
              type: previewItem.type,
              duration: previewItem.duration ?? null,
              description: previewItem.description ?? null,
              details: previewItem.details ?? null,
              applicable_coupons: previewItem.applicable_coupons ?? [],
            }
          : { id: surgery.id, name: surgery.name, type: surgery.type },
        price: Number(previewItem?.final_price ?? surgery.price),
        originalPrice: Number(previewItem?.price ?? surgery.price),
        discountPercent: Number(previewItem?.discount_percent ?? 0),
        selectedCouponId: selectedSurgeryCouponMap?.[surgery.id]?.couponId,
      };
    });
  const purchaseOptionsAddOnIds = selectedPurchaseAddOnIds ?? [];
  const isVaccinationService = isVaccinationServiceType(filters?.service_id);
  const showVaccineSection = !isSelectedServiceVC && !isVideoConsultationServiceType(filters?.service_id);
  const showAddOnDiscount = isAddOnDiscountEnabled({
    subServiceId: filters?.sub_service_id,
    serviceTypeId: filters?.service_type_id,
  });
  const previewVaccineItems = getPreviewVaccineItems(previewCartItems);
  const diagnosticCartItems = resolveDiagnosticCartItems({
    previewCartItems,
    summaryData: rescheduleFlag ? rescheduleBookingSummaryData : null,
  });
  const selectedDiagnosticIds = selectedPurchaseDiagnosticIds ?? [];
  const bookingDiagnosticItems = diagnosticCartItems.filter(
    (item) => !selectedDiagnosticIds.includes(item.id) && !(selectedPurchaseOtherTestIds ?? []).includes(item.id),
  );
  const diagnosticItems = useMemo(
    () =>
      (purchaseOptionsData?.diagnostics ?? []).map(({ id, name, price }) => ({
        id,
        title: name,
        state: {
          rightLabel: <span>₹{Math.round(Number(price ?? 0))}</span>,
        },
      })),
    [purchaseOptionsData?.diagnostics],
  );
  const selectedDiagnosticItems = useMemo(
    () => diagnosticItems.filter((item) => (selectedPurchaseDiagnosticIds ?? []).includes(item.id)),
    [diagnosticItems, selectedPurchaseDiagnosticIds],
  );
  const selectedDiagnosticCards = (purchaseOptionsData?.diagnostics ?? [])
    .filter((item) => selectedDiagnosticIds.includes(item.id))
    .map((option) => {
      const source = previewCartItems.find((item) => item.id === option.id) ?? option;
      const price = Number(source.final_price ?? 0);
      return {
        option: {
          id: source.id,
          name: source.name,
          type: DIAGNOSTIC_CART_ITEM_TYPE,
          duration: source.duration ?? null,
          description: source.description ?? null,
          details: source.details ?? null,
          applicable_coupons: [],
        } as ISurgeryCardOption,
        price,
        originalPrice: Number(source.price ?? price),
        discountPercent: Number(source.discount_percent ?? 0),
      };
    });
  const onSelectDiagnostics = (items: { id: string; title?: string }[]) => {
    const diagnosticIds = items.map((item) => item.id);
    dispatch(setSelectedPurchaseDiagnosticIds(diagnosticIds));
    logBookingEvent("DIAGNOSTIC_SELECTED", {
      cta_text: items.map((item) => item.title).join(", ") || "none",
      filter_values: {
        diagnostic_ids: diagnosticIds,
        diagnostic_names: items.map((item) => item.title ?? null),
        service_id: filters?.service_id || null,
      },
    });
  };
  const onRemoveDiagnostic = (diagnosticId: string) => {
    onSelectDiagnostics(selectedDiagnosticItems.filter((item) => item.id !== diagnosticId));
  };
  const selectedOtherTestIds = selectedPurchaseOtherTestIds ?? [];
  const otherTestItems = useMemo(
    () =>
      (purchaseOptionsData?.other_diagnostic_test ?? []).map(({ id, name, price }) => ({
        id,
        title: name,
        state: {
          rightLabel: <span>₹{Math.round(Number(price ?? 0))}</span>,
        },
      })),
    [purchaseOptionsData?.other_diagnostic_test],
  );
  const selectedOtherTestItems = useMemo(
    () => otherTestItems.filter((item) => selectedOtherTestIds.includes(item.id)),
    [otherTestItems, selectedOtherTestIds],
  );
  const selectedOtherTestCards = (purchaseOptionsData?.other_diagnostic_test ?? [])
    .filter((item) => selectedOtherTestIds.includes(item.id))
    .map((option) => {
      const source = previewCartItems.find((item) => item.id === option.id) ?? option;
      const price = Number(source.final_price ?? 0);
      return {
        option: {
          id: source.id,
          name: source.name,
          type: "other_diagnostic_test",
          duration: source.duration ?? null,
          description: source.description ?? null,
          details: source.details ?? null,
          applicable_coupons: [],
        } as ISurgeryCardOption,
        price,
        originalPrice: Number(source.price ?? price),
        discountPercent: Number(source.discount_percent ?? 0),
      };
    });
  const onSelectOtherTests = (items: { id: string; title?: string }[]) => {
    const otherTestIds = items.map((item) => item.id);
    dispatch(setSelectedPurchaseOtherTestIds(otherTestIds));
    logBookingEvent("OTHER_TEST_SELECTED", {
      cta_text: items.map((item) => item.title).join(", ") || "none",
      filter_values: {
        other_test_ids: otherTestIds,
        other_test_names: items.map((item) => item.title ?? null),
        service_id: filters?.service_id || null,
      },
    });
  };
  const onRemoveOtherTest = (otherTestId: string) => {
    onSelectOtherTests(selectedOtherTestItems.filter((item) => item.id !== otherTestId));
  };
  const vaccineCouponMap = selectedVaccineCouponMap ?? {};
  const isVaccineSelectionMissing = isVaccinationService && (selectedPurchaseVaccineIds ?? []).length === 0;
  const isDiagnosticSelectionMissing = !rescheduleFlag && diagnosticItems.length > 0 && selectedDiagnosticItems.length === 0;
  const isSurgerySelectionMissing = isSurgeryService && surgeryItems.length > 0 && selectedSurgeryItems.length === 0;
  const diagnosticServiceNames = [
    getSubService(filters?.service_id, filters?.sub_service_id)?.sub_service_name,
    selectedServicePurchaseOption?.name,
    selectedService?.service_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const diagnosticSectionLabel = /x[\s-]?ray/.test(diagnosticServiceNames)
    ? "X-ray line item"
    : diagnosticServiceNames.includes("ultrasound")
      ? "Ultrasound line item"
      : "Diagnostic tests";
  const onToggleAddOn = (addOnId: string) => {
    // Toggle flips the id in/out, so read the pre-dispatch state to know the direction.
    const isRemoving = (selectedPurchaseAddOnIds ?? []).includes(addOnId);
    dispatch(toggleSelectedPurchaseAddOnId(addOnId));
    logBookingEvent("ADDON_TOGGLED", {
      cta_text: addOnId,
      checkbox_text: isRemoving ? "Removed" : "Added",
      filter_values: {
        add_on_id: addOnId,
        action: isRemoving ? "Removed" : "Added",
        service_id: filters?.service_id || null,
      },
    });
  };
  const onToggleVaccine = (vaccineId: string) => {
    // Toggle flips the id in/out, so read the pre-dispatch state to know the direction.
    const isRemoving = (selectedPurchaseVaccineIds ?? []).includes(vaccineId);
    dispatch(toggleSelectedPurchaseVaccineId(vaccineId));
    requestBookingCartPreview(true);
    logBookingEvent("VACCINE_TOGGLED", {
      cta_text: vaccineId,
      checkbox_text: isRemoving ? "Removed" : "Added",
      filter_values: {
        vaccine_id: vaccineId,
        action: isRemoving ? "Removed" : "Added",
        service_id: filters?.service_id || null,
      },
    });
  };
  
  const logDiscountCouponSelected = (couponName?: string, couponId?: string | null, vaccineId?: string) => {
    logBookingEvent("DISCOUNT_COUPON_SELECTED", {
      cta_text: couponName || couponId || "none",
      filter_values: {
        coupon_name: couponName || null,
        coupon_id: couponId || null,
        service_id: filters?.service_id || null,
        ...(vaccineId ? { vaccine_id: vaccineId } : {}),
      },
    });
  };
  const onSelectVaccineCoupon = (vaccineId: string, couponId: string | null, couponName?: string) => {
    dispatch(setSelectedVaccineCoupon({ vaccineId, couponId }));
    requestBookingCartPreview(true);
    logDiscountCouponSelected(couponName, couponId, vaccineId);
  };
  const onSelectAddOnCoupon = (addOnId: string, couponId: string | null, couponName?: string) => {
    dispatch(setSelectedAddOnCoupon({ addOnId, couponId }));
    requestBookingCartPreview(true, undefined, undefined, undefined, undefined, { addOnId, couponId });
    logDiscountCouponSelected(couponName, couponId);
  };
  const onSelectMembership = (membershipId: string | null, membershipTitle?: string) => {
    dispatch(setSelectedPurchaseMembershipId(membershipId));
    logBookingEvent("MEMBERSHIP_SELECTED", {
      cta_text: membershipTitle || membershipId || "none",
      membership_id: membershipId || undefined,
      filter_values: {
        membership_id: membershipId || null,
        membership_name: membershipTitle || null,
        service_id: filters?.service_id || null,
      },
    });
    if (!membershipId) {
      formHook.setValue("membershipName", "");
      return;
    }
    if (membershipTitle) {
      formHook.setValue("membershipName", membershipTitle);
    }
  };
  const onSelectSurgeries = (items: { id: string; title?: string }[]) => {
    const surgeryIds = items.map((item) => item.id);
    dispatch(setSelectedSurgeryIds(surgeryIds));
    logBookingEvent("SURGERY_SELECTED", {
      cta_text: items.map((item) => item.title).join(", ") || "none",
      filter_values: {
        surgery_ids: surgeryIds,
        surgery_names: items.map((item) => item.title ?? null),
        service_id: filters?.service_id || null,
      },
    });
  };
  const onRemoveSurgery = (surgeryId: string) => {
    onSelectSurgeries(selectedSurgeryItems.filter((item) => item.id !== surgeryId));
  };
  const selectedAddOnOptions =
    purchaseOptionsData?.service_add_ons?.filter((addOn) => purchaseOptionsAddOnIds.includes(addOn.id)) ?? [];
  const selectedAddOnIdsSet = new Set(purchaseOptionsAddOnIds);
  const selectedMembershipPrice = isSelectedMembershipBilled
    ? 0
    : Number(previewMembershipItem?.final_price ?? selectedMembershipOption?.final_price ?? 0);
  const selectedMembershipOriginalPrice = Number(
    isSelectedMembershipBilled
      ? 0
      : (previewMembershipItem?.price ?? selectedMembershipOption?.price ?? selectedMembershipPrice),
  );
  const fallbackOriginalTotal =
    selectedServiceOriginalPrice +
    selectedMembershipOriginalPrice +
    selectedAddOnOptions.reduce((sum, item) => sum + Number(item.price ?? 0), 0);
  const fallbackTotalToPay =
    selectedServicePrice +
    selectedMembershipPrice +
    selectedAddOnOptions.reduce((sum, item) => sum + Number(item.final_price ?? 0), 0);
  const billingDetails = bookingCartPreviewData?.billing_details;
  const computedAdvancePayment = Number(
    billingDetails?.amount_to_pay_now ?? billingDetails?.booking_fee ?? selectedSlotDetails?.booking_fee ?? 0,
  );
  const isPaidBooking = selectedSlotDetails?.prepaid_booking_slot?.toLowerCase() === "yes" && computedAdvancePayment > 0;
  const isPurchaseSummaryLoading =
    (isPurchaseOptionsInProgress && !purchaseOptionsData) ||
    // Hide the old slot's prices while the new slot is repriced.
    (isBookingCartPreviewInProgress &&
      (!bookingCartPreviewData || previewSurgeKey !== surgeKey(surgeTime)));
  const modalTotalSummary = {
    total: Number(billingDetails?.total_amount_to_pay ?? fallbackTotalToPay),
    original: Number(billingDetails?.total_price ?? fallbackOriginalTotal),
    discountAmount: Number(
      billingDetails?.total_discount ?? Math.max(0, fallbackOriginalTotal - fallbackTotalToPay),
    ),
    amountToPayNow: Number(billingDetails?.amount_to_pay_now ?? 0),
    amountToPayAtClinic: Number(billingDetails?.amount_to_pay_at_clinic ?? 0),
  };
  const modalTotalDiscount = billingDetails?.total_discount_percent ?? 0;
  const couponPlaceholder = !selectedCouponLabel && previewServiceItem?.coupon_name?.trim();
  const inlineCouponPlaceholder = couponPlaceholder || "Select Coupon";
  const modalCouponPlaceholder = couponPlaceholder || "Apply Discount";
  const membershipCardOption =
    previewMembershipItem || selectedMembershipOption || selectedSummaryMembershipItem || purchaseOptionsData?.memberships?.[0] || null;
  const membershipCardSelected = membershipCardOption?.id === selectedPurchaseMembershipId;
  const membershipCardBillingStatus =
    (rescheduleBookingSummaryData?.addons?.memberships ?? []).find(
      (membership) => membership.id === membershipCardOption?.id,
    )?.billing_status ?? null;
  const membershipCardPrice = Number(membershipCardOption?.final_price ?? 0);
  const membershipCardOriginalPrice = Number(membershipCardOption?.price ?? membershipCardPrice);
  const membershipCardPerSession = membershipCardOption?.duration
    ? `${membershipCardOption.duration} min session`
    : membershipCardOption?.description || "Membership benefits available";
  const waivedOffAmount = Math.max(0, Math.round(membershipCardOriginalPrice - membershipCardPrice));
  const waivedOffLabel =
    waivedOffAmount > 0
      ? `${formatAmount(waivedOffAmount)} waived off on membership purchase`
      : "Membership benefits available on purchase";
  const purchaseOptionAddOnCards =
    purchaseOptionsData?.service_add_ons?.map((addon) => {
      const previewAddOn = previewAddOnItemsMap.get(addon.id);
      return {
        ...addon,
        ...(previewAddOn ?? {}),
        type: (previewAddOn?.type || "add_on_service") as string,
        image_url: previewAddOn?.image_url ?? addon?.image_url,
        isAdded: selectedAddOnIdsSet.has(addon.id),
      };
    }) ?? [];
  const previewFallbackAddOnCards = Array.from(previewAddOnItemsMap.values())
    .filter((previewAddOn) => selectedAddOnIdsSet.has(previewAddOn.id))
    .map((previewAddOn) => ({
      ...previewAddOn,
      type: (previewAddOn?.type || "add_on_service") as string,
      image_url: previewAddOn?.image_url,
      previously_added: false,
      isAdded: true,
    }));
  const summaryFallbackAddOnCards = [
    ...(rescheduleBookingSummaryData?.addons?.add_on_services ?? []),
    ...(rescheduleBookingSummaryData?.addons?.add_on_boms ?? []),
  ]
    .filter((addOn) => selectedAddOnIdsSet.has(addOn.id))
    .map((addOn) => ({
      id: addOn.id,
      type: "add_on_service",
      name: addOn.name,
      qty: 1,
      price: Number(addOn.price ?? 0),
      discount: Number(addOn.discount ?? 0),
      discount_percent: Number(addOn.discount_percent ?? 0),
      final_price: Number(addOn.final_price ?? addOn.price ?? 0),
      duration: addOn.duration ?? null,
      description: addOn.description ?? null,
      image_url: addOn?.image_url,
      gift_id: addOn.gift_id,
      gift_coupon_id: addOn.gift_coupon_id,
      offer_callout: addOn.offer_callout,
      previously_added: false,
      isAdded: true,
    }));
  const fallbackAddOnCards = [...previewFallbackAddOnCards];
  const previewFallbackAddOnIds = new Set(previewFallbackAddOnCards.map((addOn) => addOn.id));
  summaryFallbackAddOnCards.forEach((addOn) => {
    if (!previewFallbackAddOnIds.has(addOn.id)) {
      fallbackAddOnCards.push(addOn);
    }
  });
  const purchaseOptionAddOnIds = new Set(purchaseOptionAddOnCards.map((addOn) => addOn.id));
  const serviceAddOnCards =
    purchaseOptionAddOnCards.length > 0
      ? [
        ...purchaseOptionAddOnCards,
        ...fallbackAddOnCards.filter((addOn) => !purchaseOptionAddOnIds.has(addOn.id)),
      ]
      : fallbackAddOnCards;
  const upgradeTitle = `Upgrade ${petDetails?.name || "Pet"}'s ${previewServiceItem?.name || selectedServicePurchaseOption?.name || selectedService?.service_name || "Service"}`;
  const hasPurchaseOptionsFetchDependencies = Boolean(filters?.sub_service_id && selectedSlotsClinicDetails?.clinic_id && customerId);
  const isPurchaseOptionsBootstrapping =
    shouldFetchPurchaseOptions &&
    hasPurchaseOptionsFetchDependencies &&
    (isPurchaseOptionsInProgress || purchaseOptionsReadyKey === null);
  const shouldShowModalLoader = open && (!hasModalBlinkDelayPassed || isPurchaseOptionsBootstrapping);

  useEffect(() => {
    if (!open) {
      setHasModalBlinkDelayPassed(false);
      return;
    }

    const blinkTimer = setTimeout(() => {
      setHasModalBlinkDelayPassed(true);
    }, 180);

    return () => {
      clearTimeout(blinkTimer);
    };
  }, [open]);

  if (shouldRenderInline) {
    return (
      <div className="rounded-[24px] border border-[#E6E8ED] bg-white">
        <form onSubmit={formHook.handleSubmit(onSubmit)} className="space-y-4 p-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)] lg:items-center">
            <div className="text-sm text-[#1E2431]">
              <MandatoryLabel label={"Discount Coupons"} />
            </div>
            <div>
              <SearchableSelectMenu
                errorMessages={[{ message: "", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("coupon", item.title);
                    formHook.setValue("couponId", item.id);
                    requestBookingCartPreview(true, item.id);
                  } else {
                    formHook.setValue("coupon", "");
                    formHook.setValue("couponId", undefined);
                    requestBookingCartPreview(true, null);
                  }
                  logDiscountCouponSelected(item?.title, item?.id);
                }}
                filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
                onEnterPress={(typedValue) => {
                  const exactMatch = couponsData?.find(
                    (c) => c.coupon_name?.toLowerCase() === typedValue?.toLowerCase(),
                  );
                  if (exactMatch) {
                    const currentSelected = formHook.getValues("couponId");
                    if (exactMatch.coupon_id === currentSelected) return;
                    formHook.setValue("coupon", exactMatch.coupon_name);
                    formHook.setValue("couponId", exactMatch.coupon_id);
                    requestBookingCartPreview(true, exactMatch.coupon_id);
                    logDiscountCouponSelected(exactMatch.coupon_name, exactMatch.coupon_id);
                  } else {
                    toast.error("Coupon not applicable");
                    formHook.setValue("coupon", "");
                    formHook.setValue("couponId", undefined);
                    requestBookingCartPreview(true, null);
                    logDiscountCouponSelected();
                  }
                }}
                fieldError={formHook.formState.errors.coupon}
                register={formHook.register("coupon", { required: false })}
                selectItems={couponsItem}
                placeholder={inlineCouponPlaceholder}
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                withReset={true}
                listZIndex="z-20"
                defaultSelected={couponsItem?.filter((oc) => oc.title === formHook.watch("coupon"))[0]}
                className="text-sm text-[#3B4250]"
                inputContainerClassName="h-[44px] border-[#D8DBE2] rounded-xl bg-white"
                containerClassName="w-full"
              />
            </div>
          </div>

          <Suspense fallback={<PurchaseOptionsSkeleton />}>
            <PurchaseOptionsBlock
              isLoading={isPurchaseSummaryLoading}
              serviceItem={selectedServicePurchaseOption}
              addOns={purchaseOptionsData?.service_add_ons ?? []}
              memberships={purchaseOptionsData?.memberships ?? []}
              selectedAddOnIds={purchaseOptionsAddOnIds}
              selectedMembershipId={selectedPurchaseMembershipId ?? null}
              onToggleAddOn={onToggleAddOn}
              onSelectMembership={onSelectMembership}
            />
          </Suspense>

          <section className="space-y-4 rounded-xl border border-[#E6E8ED] px-4 py-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-[#1F2431]">Vaccination Diary</p>
              <div className="flex items-center gap-3">
                <input hidden {...formHook.register("vaccination", { required: false })} />
                <Switch
                  checked={vaccinationSelected}
                  onChange={(checked: boolean) => {
                    formHook.setValue("vaccination", checked);
                    logBookingEvent("VACCINATION_DIARY_TOGGLE", {
                      cta_text: checked ? "Enabled" : "Disabled",
                    });
                  }}
                  className={`${vaccinationSelected ? "bg-[#F45E79]" : "bg-[#DDE2E9]"} relative inline-flex h-8 w-[64px] items-center rounded-full transition-colors`}
                >
                  <span className="sr-only">Vaccination diary</span>
                  <span
                    className={`${vaccinationSelected ? "translate-x-[34px]" : "translate-x-1"} inline-flex h-6 w-6 transform items-center justify-center rounded-full bg-white shadow transition`}
                  >
                    {vaccinationSelected && <CheckCircleIcon className="h-5 w-5 text-[#F45E79]" />}
                  </span>
                </Switch>
              </div>
            </div>
          </section>

          {/* <RBACFlag actionId="EDIT_BOOKING_COMMENT_PERMISION">
            <section className="rounded-xl border border-[#E6E8ED] px-4 py-4">
              <label htmlFor="comment-inline" className="mb-2 block text-sm font-semibold text-[#1F2431]">
                Any special requirements? <span className="font-normal">(Optional)</span>
              </label>
              <textarea
                id="comment-inline"
                rows={4}
                {...formHook.register("otherComment", { required: false })}
                placeholder="Write anything if required or any pet parents concerns here"
                className="block w-full resize-none rounded-xl border border-[#D9DDE5] bg-[#F8F9FB] px-3 py-2.5 text-sm text-[#1F2431] shadow-sm focus:border-[#B7BECB] focus:outline-none focus:ring-0"
                defaultValue={""}
              />
            </section>
          </RBACFlag> */}

          {!isVCBookingNotAllowed && (
            <div className="flex justify-end">
              <button
                disabled={!visibleButton() || isVCSelectionInvalid || isVaccineSelectionMissing || isDiagnosticSelectionMissing || isSurgerySelectionMissing}
                type="submit"
                className={`h-[48px] w-full rounded-xl px-6 text-base font-semibold text-white transition lg:w-[300px] ${visibleButton() && !isVCSelectionInvalid && !isVaccineSelectionMissing && !isDiagnosticSelectionMissing && !isSurgerySelectionMissing ? "bg-gradient-to-r from-[#1D1F24] to-[#272B33]" : "bg-[#9DA3AF]"
                  }`}
              >
                {commentEdit ? "Update" : "Continue to Preview"}
              </button>
            </div>
          )}
        </form>
      </div>
    );
  }
  return (
    <>
      {commentEdit ? (
        hideTrigger ? null : (
          <div
            className="cursor-pointer"
            onClick={() => {
              logBookingEvent("COMMENT_EDIT_CLICK", { scr_id: "BOOKING_PREVIEW" });
              setOpen(true);
            }}
          >
            {" "}
            <img src={editicon} alt="edit" className="h-4 w-4" />
          </div>
        )
      ) : overBooking ? (
        <button
          type="button"
          disabled={!activeButton}
          className={`${activeButton ? "bg-[#000000]" : "bg-gray-500"
            } text-white  py-1.5 rounded-md px-2 cursor-pointer text-xs`}
          onClick={async () => {
            // Overbooking: validate the row form and commit slot/filter state to redux
            // BEFORE opening — the modal's data fetches depend on that state being set.
            if (onClick) {
              const canProceed = await onClick();
              if (canProceed === false) return;
            }
            if (rescheduleBookingIdFromParams) {
              fetchBookingSummary(rescheduleBookingIdFromParams);
            }
            setOpen(true);
          }}
        >
          {buttonName}
        </button>
      ) : (
        <div className="flex flex-col  ">
          {isPaidBooking && (
            <button className="bg-[#ffeda6] text-[#C37307] py-2 text-xs font-medium rounded-md fixed bottom-16 text-center w-[-webkit-fill-available] flex justify-center items-center gap-2">
              <span>
                <GrCircleAlert className="h-4 w-4" />
              </span>{" "}
              An Advance Payment will be required for this slot. The payment link will be sent to the customer after you
              book this slot.
            </button>
          )}
          {!isVCBookingNotAllowed && (
            <button
              disabled={!activeButton}
              className={`${activeButton ? "bg-[#000000]" : "bg-gray-500"
                } text-white  py-2.5 rounded-md fixed bottom-3 text-center w-[-webkit-fill-available] cursor-pointer`}
              // onClick={() => setOpen(true)}
              onClick={handelClick}
            >
              {buttonName}
            </button>
          )}
        </div>
      )}

      {nudgeAvailable ? (
        <NudgesContainer
          source="booking_journey"
          alert_category_list={alert_category_list}
          customer_id={customerId}
          pet_Id={petId}
          staff_id={selectedSlotStaffDetails?.staff_id}
          slot_start_time={selectedSlotDetails?.start_time}
          slot_end_time={selectedSlotDetails?.end_time}
          clinic_id={selectedSlotsClinicDetails?.clinic_id}
          journey_point="booking_journey"
          setIsNotPreferedDoctorNudgeOpen={setIsNotPreferedDoctorNudgeOpen}
        />
      ) : (
        <Transition.Root show={open} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-[1200] overflow-y-auto"
            initialFocus={cancelButtonRef}
            onClose={() => setOpen(false)}
          >
            <div className="relative z-10 flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 z-0 transition-opacity bg-gray-500 bg-opacity-75" />
              </Transition.Child>

              {/* This element is to trick the browser into centering the modal contents. */}
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                &#8203;
              </span>

              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className="relative z-10 inline-block w-full max-w-[1050px] align-bottom text-left shadow-xl transition-all sm:my-8 sm:align-middle"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex max-h-[92vh] flex-col overflow-hidden rounded-[24px] bg-white">
                    {shouldShowModalLoader ? (
                      <CommentSectionModalLoader />
                    ) : (
                      <form onSubmit={formHook.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
                        <div
                          className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-4 "
                          style={{ overflowY: "overlay", scrollbarWidth: "none", msOverflowStyle: "none" }}
                        >
                          <ServiceSummaryCard
                            isLoading={isPurchaseSummaryLoading}
                            upgradeTitle={upgradeTitle}
                            closeNode={
                              <button
                                ref={cancelButtonRef}
                                type="button"
                                className="rounded-md p-1 text-[#232932] transition hover:bg-[#F4F5F8]"
                                onClick={() => setOpen(false)}
                              >
                                <XMarkIcon className="h-6 w-6" />
                              </button>
                            }
                            searchNode={
                              <div className="relative w-full max-w-[510px]">
                                <UnifiedCartProductSearchComponent
                                  placeholder="Find Other Service Prices"
                                  isOnlyServiceItem
                                  showStatusTags={false}
                                  containerClassName="w-full"
                                  inputContainerClassName="w-full h-[40px] rounded-[10px] bg-white
    [&>input]:h-full [&>input]:py-0 [&>input]:pl-1 [&>input]:pr-10
    [&>input]:text-[12px] [&>input]:font-medium [&>input]:text-[#3B4250]
    [&>input]:placeholder:text-[12px] [&>input]:placeholder:font-medium [&>input]:placeholder:text-[#6C727F]"
                                  itemActiveClassName="bg-[#1F2937] text-white"
                                  onSelectItem={() => {
                                    // Showcase only: keep dropdown experience without binding selection to form state.
                                  }}
                                />
                              </div>
                            }
                            customerName={customerDetails?.name || "NA"}
                            petName={petDetails?.name || "NA"}
                            pet_species_type_id={petDetails?.pet_species_type_id || "NA"}
                            petWeightLabel={petDetails?.weight ? `${petDetails?.weight} KG` : "NA"}
                            serviceName={selectedServicePurchaseOption?.name || selectedService?.service_name || "Consultation"}
                            serviceTypeName={selectedService?.service_type_name}
                            serviceOfferCallout={serviceOfferCallout}
                            servicePriceLabel={formatAmount(selectedServicePrice)}
                            serviceDiscount={selectedServiceDiscount}
                            serviceOriginalPriceLabel={formatAmount(selectedServiceOriginalPrice)}
                            slotStartTimeLabel={convert24HrsTo12Hrs(selectedSlotDetails?.start_time) || "--"}
                            bookingDateLabel={bookingDateLabel}
                            staffName={selectedSlotStaffDetails?.staff_name || "NA"}
                            staffExperience={selectedSlotStaffDetails?.experience}
                            waivedOffLabel={waivedOffLabel}
                            chipData={billingDetails}
                            followUpChip={previewServiceItem?.follow_up_chip_details}
                            subServiceId={filters?.sub_service_id}
                            clinicId={selectedSlotsClinicDetails?.clinic_id}
                            userId={customerId}
                            petId={petId}
                            couponId={selectedCouponId}
                            surgeTime={surgeTime}
                            isHomeService={isHomeServiceSelection}
                            clinicName={selectedClinicName}
                            visitAddress={selectedVisitAddress}
                            travelTimeLabel={estimatedTravelTimeLabel}
                            onClickEditVisitAddress={handleEditVisitAddress}
                            isPaidBooking={isPaidBooking}
                            bookingFee={computedAdvancePayment}
                            onPetWeightSaved={() => requestBookingCartPreview(true)}
                          />

                          {diagnosticItems.length > 0 && (
                            <section className="space-y-4 px-4 py-[8px]">
                              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)] lg:items-center">
                                <div className="text-[14px] font-semibold text-[#222222]">
                                  <MandatoryLabel label={diagnosticSectionLabel} isMandatory={!rescheduleFlag} />
                                  {!rescheduleFlag && <p className="mt-0.5 text-[11px] font-normal text-[#6C727F]">Select at least 1</p>}
                                </div>
                                <div>
                                  <SearchableMultiSelectMenu
                                    errorMessages={[]}
                                    onSelectItem={(items) => onSelectDiagnostics(items ?? [])}
                                    fieldError={undefined}
                                    register={{} as any}
                                    selectItems={diagnosticItems}
                                    placeholder={`Select ${diagnosticSectionLabel}`}
                                    showTooltips={true}
                                    showTypedErrors={false}
                                    showDropdownIcon={true}
                                    listZIndex="z-20"
                                    showDropdownWhenNoData={true}
                                    noDataDropDownContent={
                                      isPurchaseOptionsInProgress ? (
                                        <span className="text-center text-[10px] sm:text-xs">Loading diagnostics...</span>
                                      ) : (
                                        <span className="text-center text-[10px] sm:text-xs">No diagnostics found</span>
                                      )
                                    }
                                    defaultSelected={selectedDiagnosticItems}
                                    className="text-[12px] font-medium text-[#3B4250] placeholder:text-[12px] placeholder:font-medium placeholder:text-[#6C727F]"
                                    containerClassName="w-full"
                                  />
                                </div>
                              </div>
                              {selectedDiagnosticCards.map((card) => (
                                <SurgeryOptionCard
                                  key={card.option.id}
                                  isLoading={isPurchaseSummaryLoading}
                                  surgeryOption={card.option}
                                  surgeryPrice={card.price}
                                  surgeryOriginalPrice={card.originalPrice}
                                  surgeryDiscountPercent={card.discountPercent}
                                  isSelected={true}
                                  formatAmount={formatAmount}
                                  onAdd={() => undefined}
                                  onRemove={() => onRemoveDiagnostic(card.option.id)}
                                  fallbackLabel="Diagnostic test"
                                />
                              ))}
                            </section>
                          )}
                          {otherTestItems?.length > 0 && (
                            <section className="space-y-4 px-4 py-[8px]">
                              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)] lg:items-center">
                                <div className="text-[14px] font-semibold text-[#222222]">
                                  <MandatoryLabel label="Other Tests" />
                                </div>
                                <div>
                                  <SearchableMultiSelectMenu
                                    errorMessages={[]}
                                    onSelectItem={(items) => onSelectOtherTests(items ?? [])}
                                    fieldError={undefined}
                                    register={{} as any}
                                    selectItems={otherTestItems}
                                    placeholder="Select Other Tests"
                                    showTooltips={true}
                                    showTypedErrors={false}
                                    showDropdownIcon={true}
                                    listZIndex="z-20"
                                    showDropdownWhenNoData={true}
                                    noDataDropDownContent={
                                      isPurchaseOptionsInProgress ? (
                                        <span className="text-center text-[10px] sm:text-xs">Loading tests...</span>
                                      ) : (
                                        <span className="text-center text-[10px] sm:text-xs">No tests found</span>
                                      )
                                    }
                                    defaultSelected={selectedOtherTestItems}
                                    className="text-[12px] font-medium text-[#3B4250] placeholder:text-[12px] placeholder:font-medium placeholder:text-[#6C727F]"
                                    containerClassName="w-full"
                                  />
                                </div>
                              </div>
                              {selectedOtherTestCards?.map((card) => (
                                <SurgeryOptionCard
                                  key={card?.option?.id}
                                  isLoading={isPurchaseSummaryLoading}
                                  surgeryOption={card?.option}
                                  surgeryPrice={card?.price}
                                  surgeryOriginalPrice={card?.originalPrice}
                                  surgeryDiscountPercent={card?.discountPercent}
                                  isSelected={true}
                                  formatAmount={formatAmount}
                                  onAdd={() => undefined}
                                  onRemove={() => onRemoveOtherTest(card?.option?.id)}
                                  fallbackLabel="Other test"
                                />
                              ))}
                            </section>
                          )}
                          {isSurgeryService && (
                            <section className="space-y-4 px-4 py-[8px]">
                              <SurgerySection
                                surgeryItems={surgeryItems}
                                defaultSelected={selectedSurgeryItems}
                                isOptionsLoading={isSurgeryOptionsInProgress}
                                onSelectSurgeries={onSelectSurgeries}
                                onRemoveSurgery={onRemoveSurgery}
                                isCardLoading={isPurchaseSummaryLoading || isSurgeryOptionsInProgress}
                                surgeryCards={surgeryCards}
                                formatAmount={formatAmount}
                                onSelectCoupon={(surgeryId, couponId, couponName) => {
                                  dispatch(setSelectedSurgeryCoupon({ surgeryId, couponId: couponId ?? null, couponName }));
                                  requestBookingCartPreview(true, undefined, undefined, undefined, {
                                    surgeryId,
                                    couponId: couponId ?? null,
                                    couponName,
                                  });
                                  logBookingEvent("SURGERY_DISCOUNT_COUPON_SELECTED", {
                                    cta_text: couponName || couponId || "none",
                                    filter_values: {
                                      surgery_id: surgeryId,
                                      coupon_name: couponName || null,
                                      coupon_id: couponId || null,
                                      service_id: filters?.service_id || null,
                                    },
                                  });
                                }}
                              />
                            </section>
                          )}
                          {showVaccineSection && (
                            <ChooseVaccineSection
                              isLoading={isPurchaseSummaryLoading}
                              petName={petDetails?.name}
                              vaccines={previewVaccineItems}
                              selectedVaccineIds={selectedPurchaseVaccineIds ?? []}
                              vaccineCouponMap={vaccineCouponMap}
                              formatAmount={formatAmount}
                              onToggleVaccine={onToggleVaccine}
                              onSelectVaccineCoupon={onSelectVaccineCoupon}
                            />
                          )}

                          <section className="px-4 py-[8px]">
                            {/* {isHomeServiceSelection &&
                              <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)] lg:items-center">
                                <div className="text-[14px] font-semibold text-[#222222]">
                                  <MandatoryLabel
                                    isDisabled={customerMembershipStatus}
                                    label={"Reason For Appointment"}
                                    isMandatory
                                  />
                                </div>

                                <div className="w-full">
                                  <SearchableSelectMenu
                                    errorMessages={[{ message: "Reason For Appointment is required", type: "required" }]}
                                    onSelectItem={(item) => {
                                      formHook.setValue("reason", item?.title ?? "");
                                      logBookingEvent("APPOINTMENT_REASON_SELECTED", {
                                        cta_text: item?.title || "none",
                                        search_item: item?.title || undefined,
                                      });
                                    }}
                                    showDropdownIcon={true}
                                    showTooltips={false}
                                    fieldError={formHook.formState.errors.reason}
                                    register={formHook.register("reason", { required: isHomeServiceSelection })}
                                    selectItems={reasonItems}
                                    placeholder="Select Reason For Appointment"
                                    showTypedErrors={false}
                                    listZIndex="z-20"
                                    defaultSelected={reasonItems?.find(
                                      (oc) => oc.title === formHook.watch("reason") || oc.id === formHook.watch("reason"),
                                    )}
                                    className="text-[12px] font-medium text-[#3B4250] placeholder:text-[12px] placeholder:font-medium placeholder:text-[#6C727F]"
                                    inputContainerClassName="h-[40px] border-[#D8DBE2] rounded-[10px] bg-white"
                                    containerClassName="w-full"
                                  />
                                </div>

                              </div>
                            } */}
                            {!isVaccinationService && (
                              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)] lg:items-center">
                                <div className="text-[14px] font-semibold text-[#222222]">
                                  <MandatoryLabel label={"Discount Coupons"} />
                                </div>
                                <div>
                                  <SearchableSelectMenu
                                    errorMessages={[{ message: "", type: "required" }]}
                                    onSelectItem={(item) => {
                                      if (item) {
                                        formHook.setValue("coupon", item.title);
                                        formHook.setValue("couponId", item.id);
                                        requestBookingCartPreview(true, item.id);
                                      } else {
                                        formHook.setValue("coupon", "");
                                        formHook.setValue("couponId", undefined);
                                        requestBookingCartPreview(true, null);
                                      }
                                      logDiscountCouponSelected(item?.title, item?.id);
                                    }}
                                    filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
                                    onEnterPress={(typedValue) => {
                                      const exactMatch = couponsData?.find(
                                        (c) => c.coupon_name?.toLowerCase() === typedValue?.toLowerCase(),
                                      );
                                      if (exactMatch) {
                                        const currentSelected = formHook.getValues("couponId");
                                        if (exactMatch.coupon_id === currentSelected) return;
                                        formHook.setValue("coupon", exactMatch.coupon_name);
                                        formHook.setValue("couponId", exactMatch.coupon_id);
                                        requestBookingCartPreview(true, exactMatch.coupon_id);
                                        logDiscountCouponSelected(exactMatch.coupon_name, exactMatch.coupon_id);
                                      } else {
                                        toast.error("Coupon not applicable");
                                        formHook.setValue("coupon", "");
                                        formHook.setValue("couponId", undefined);
                                        requestBookingCartPreview(true, null);
                                        logDiscountCouponSelected();
                                      }
                                    }}
                                    fieldError={formHook.formState.errors.coupon}
                                    register={formHook.register("coupon", { required: false })}
                                    selectItems={couponsItem}
                                    placeholder={modalCouponPlaceholder}
                                    showTooltips={true}
                                    showTypedErrors={false}
                                    showDropdownIcon={true}
                                    withReset={true}
                                    listZIndex="z-20"
                                    defaultSelected={
                                      couponsItem?.filter((oc) => oc.title === formHook.watch("coupon"))[0]
                                    }
                                    className="text-[12px] font-medium text-[#3B4250] placeholder:text-[12px] placeholder:font-medium placeholder:text-[#6C727F]"
                                    inputContainerClassName="h-[40px] border-[#D8DBE2] rounded-[10px] bg-white"
                                    containerClassName="w-full"
                                  />
                                </div>
                              </div>
                            )}

                            <div className="my-4 border-t border-dashed border-[#E6E8ED]" />

                            {membershipDropdowns.map(
                              ({ key, label, placeholder, emptyLabel, selectItems, defaultSelected, wrapperClassName }) => (
                                <div
                                  key={key}
                                  className={`grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)] lg:items-center ${wrapperClassName}`}
                                >
                                  <div className="text-[14px] font-semibold text-[#222222]">
                                    <MandatoryLabel label={label} />
                                  </div>
                                  <div>
                                    <SearchableSelectMenu
                                      errorMessages={[{ message: "", type: "required" }]}
                                      onSelectItem={(item) => {
                                        const selectedMembershipName =
                                          (item?.state?.membershipName as string) || item?.title;
                                        onSelectMembership(item?.id ?? null, selectedMembershipName);
                                      }}
                                      fieldError={formHook.formState.errors.membershipName}
                                      register={formHook.register("membershipName", { required: false })}
                                      selectItems={selectItems}
                                      placeholder={placeholder}
                                      showTooltips={true}
                                      showTypedErrors={false}
                                      showDropdownIcon={true}
                                      withReset={true}
                                      listZIndex="z-20"
                                      showDropdownWhenNoData={true}
                                      dropdownNoDataContent={
                                        isMembershipOptionsInProgress ? (
                                          <span className="text-center text-[10px] sm:text-xs">
                                            Loading memberships...
                                          </span>
                                        ) : (
                                          <span className="text-center text-[10px] sm:text-xs">{emptyLabel}</span>
                                        )
                                      }
                                      defaultSelected={defaultSelected}
                                      className="text-[12px] font-medium text-[#3B4250] placeholder:text-[12px] placeholder:font-medium placeholder:text-[#6C727F]"
                                      inputContainerClassName="h-[40px] border-[#D8DBE2] rounded-[10px] bg-white"
                                      containerClassName="w-full"
                                    />
                                  </div>
                                </div>
                              ),
                            )}

                            <MembershipPlanCard
                              isLoading={isPurchaseSummaryLoading}
                              membershipOption={membershipCardOption as any}
                              membershipBillingStatus={membershipCardBillingStatus}
                              membershipPerSessionLabel={membershipCardPerSession}
                              membershipPrice={membershipCardPrice}
                              membershipOriginalPrice={membershipCardOriginalPrice}
                              isSelected={membershipCardSelected}
                              formatAmount={formatAmount}
                              onAdd={(membershipId, membershipName) =>
                                onSelectMembership(membershipId, membershipName)
                              }
                              onRemove={() => onSelectMembership(null)}
                              selectedCouponId={selectedMembershipCouponId}
                              onSelectCoupon={(couponId, couponName) => {
                                dispatch(setSelectedMembershipCouponId(couponId));
                                dispatch(setSelectedMembershipCouponName(couponName));
                                requestBookingCartPreview(true, undefined, couponId, couponName);
                                logBookingEvent("MEMBERSHIP_DISCOUNT_COUPON_SELECTED", {
                                  cta_text: couponName || couponId,
                                  membership_id: selectedPurchaseMembershipId || undefined,
                                  filter_values: {
                                    coupon_name: couponName || null,
                                    coupon_id: couponId || null,
                                    membership_id: selectedPurchaseMembershipId || null,
                                    service_id: filters?.service_id || null,
                                  },
                                });
                              }}
                            />
                          </section>

                          {serviceAddOnCards?.length > 0 && (
                            <>
                              <ServiceAddOnsSection
                                isLoading={isPurchaseSummaryLoading}
                                addOns={serviceAddOnCards as any}
                                formatAmount={formatAmount}
                                onToggleAddOn={onToggleAddOn}
                                showDiscount={showAddOnDiscount}
                                addOnCouponMap={selectedAddOnCouponMap ?? {}}
                                onSelectAddOnCoupon={onSelectAddOnCoupon}
                              />
                              <div className="my-4 border-t border-dashed border-[#E6E8ED]" />
                            </>
                          )}

                          {bookingDiagnosticItems.length > 0 && (
                            <>
                              <DiagnosticsSection
                                isLoading={isPurchaseSummaryLoading}
                                diagnostics={bookingDiagnosticItems}
                                formatAmount={formatAmount}
                              />
                              <div className="my-4 border-t border-dashed border-[#E6E8ED]" />
                            </>
                          )}

                          <section className="space-y-4 px-4">
                            <div className="flex flex-col gap-3 sm:flex-row justify-start items-center">
                              <p className="text-[14px] font-semibold text-[#1F2431]">
                                Vaccination Diary <span className="text-[#E63946]">*</span>
                              </p>
                              <div className="flex items-center gap-3">
                                <input hidden {...formHook.register("vaccination", { required: false })} />
                                <Switch
                                  checked={vaccinationSelected}
                                  onChange={(checked: boolean) => {
                                    formHook.setValue("vaccination", checked);
                                    logBookingEvent("VACCINATION_DIARY_TOGGLE", {
                                      cta_text: checked ? "Enabled" : "Disabled",
                                    });
                                  }}
                                  className={`${vaccinationSelected ? "bg-[#F45E79]" : "bg-[#DDE2E9]"} relative inline-flex h-[24px] w-[44px] items-center rounded-full transition-colors`}
                                >
                                  <span className="sr-only">Vaccination diary</span>
                                  <span
                                    className={`${vaccinationSelected ? "translate-x-[22px]" : "translate-x-1"} inline-flex h-[20px] w-[20px] transform items-center justify-center rounded-full bg-white shadow transition`}
                                  >
                                    {vaccinationSelected && <CheckCircleIcon className="h-5 w-5 text-[#F45E79]" />}
                                  </span>
                                </Switch>
                              </div>
                            </div>

                            {/* {shouldHideHygiene && (
                            <div className="flex items-center justify-between border-t border-dashed border-[#E6E8ED] pt-4">
                              <p className="text-sm font-medium text-[#1F2431]">Hygiene package add-on required</p>
                              <div>
                                <input hidden {...formHook.register("hygienePackage", { required: false })} />
                                <Toggle
                                  onToggle={(state: boolean) => {
                                    formHook.setValue("hygienePackage", state);
                                  }}
                                  checked={formHook.watch("hygienePackage")}
                                />
                              </div>
                            </div>
                          )} */}
                          </section>

                        {/* <RBACFlag actionId="EDIT_BOOKING_COMMENT_PERMISION">
                          <section className="px-4">
                            <label htmlFor="comment" className="mb-2 block text-[14px] font-medium text-[#1F2431]">
                              Any special requirements? (Optional)
                            </label>
                            <textarea
                              rows={4}
                              {...formHook.register("otherComment", { required: false })}
                              placeholder="Write anything if required or any pet parents concerns here"
                              className="block w-full resize-none rounded-[10px] border border-[#E2E2E2] bg-[#FFFFFF] px-3 py-2.5 text-[12px] text-[#1F2431] focus:border-[#E2E2E2] focus:outline-none focus:ring-0 placeholder:text-[#6C727F]"
                              defaultValue={""}
                            />
                          </section>
                        </RBACFlag> */}

                          {isVCSelectionInvalid && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                              <p className="text-xs font-medium text-red-700">{vcValidationMessage}</p>
                            </div>
                          )}
                        </div>

                        <BillingSummaryFooter
                          isLoading={isPurchaseSummaryLoading}
                          summary={modalTotalSummary}
                          totalDiscountPercent={modalTotalDiscount}
                          formatAmount={formatAmount}
                          isSubmitEnabled={visibleButton() && !isVaccineSelectionMissing && !isDiagnosticSelectionMissing && !isSurgerySelectionMissing}
                          isVCSelectionInvalid={isVCSelectionInvalid}
                          commentEdit={commentEdit}
                        />
                      </form>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}
      {isAddressListModalOpen && (
        <AddressListModal
          isAddressOpen={isAddressListModalOpen}
          onClose={() => setIsAddressListModalOpen(false)}
          closeOnOutsideClick
          payloadData={customerAddresses ?? []}
          openAddAddress={(addressId?: string) => {
            setSelectedAddressId(addressId || null);
            setIsAddressListModalOpen(false);
            setIsAddAddressModalOpen(true);
          }}
          onSelectAddress={(address) => {
            const transformedAddress = {
              title: address?.address_2,
              checkboxVisible: true,
              id: address?.address_id,
              address_id: address?.address_id,
              address_1: address?.address_1,
              address_2: address?.address_2,
              label: address?.label,
              previously_ordered: address?.previously_ordered,
              withSubItems: false,
              subSelectItems: [],
              state: {
                formattedAddress: address?.address_2,
                city: (address as any)?.city,
                state: (address as any)?.state,
                placeId: null,
                pincode: (address as any)?.pincode,
                lat: (address as any)?.latitude,
                long: (address as any)?.longitude,
              },
            };
            const latitude = (address as any)?.latitude ?? "";
            const longitude = (address as any)?.longitude ?? "";
            const constructedFilters = {
              ...(filters ?? {}),
              user_id: customerId,
              clinic_ids: [],
              staff_ids: [],
              location_details: {
                latitude: `${latitude}`,
                longitude: `${longitude}`,
              },
              city_id: null,
              pet_species_id: filters?.pet_species_id ?? petDetails?.pet_species_type_id,
              slot_status: stringConstants?.slotStatusAvailable,
            };
            dispatch(setUserAddress({ address: transformedAddress }));
            dispatch(setSlotBookingFilters({ filters: constructedFilters }));
            fetchBookingsSlotsAPI(constructedFilters);
            fetchBookedSlotsAPI({ ...constructedFilters, slot_status: "1" });
            setIsAddressListModalOpen(false);
          }}
          title="Select Home Service Address"
          
          isAtHome={isHomeServiceSelection}
        />
      )}
      {isAddAddressModalOpen && (
        <AddAddressModal
          addAddressTitle="Add Home Service Address"
          isAtHome={isHomeServiceSelection}
          closeOnOutsideClick
          onOutsideClose={() => {
            setIsAddAddressModalOpen(false);
            setSelectedAddressId(null);
            setIsAddressListModalOpen(false);
          }}
          addressId={selectedAddressId}
          onClose={() => {
            setIsAddAddressModalOpen(false);
            setSelectedAddressId(null);
            setIsAddressListModalOpen(true);
          }}
          onAddressAdded={(newAddress) => {
            if (newAddress) {
              const transformedAddress = {
                title: newAddress?.address_2,
                checkboxVisible: true,
                id: newAddress?.address_id,
                address_id: newAddress?.address_id,
                address_1: newAddress?.address_1,
                address_2: newAddress?.address_2,
                label: newAddress?.label,
                previously_ordered: newAddress?.previously_ordered,
                withSubItems: false,
                subSelectItems: [],
                state: {
                  formattedAddress: newAddress?.address_2,
                  city: newAddress?.city,
                  state: newAddress?.state,
                  placeId: null,
                  pincode: newAddress?.pincode,
                  lat: newAddress?.latitude,
                  long: newAddress?.longitude,
                },
              };
              dispatch(setUserAddress({ address: transformedAddress }));
            }
            setIsAddAddressModalOpen(false);
            setSelectedAddressId(null);
          }}
        />
      )}
    </>
  );
};

export default CommentSectionModal;
